import React, { useRef, useEffect } from 'react'
import { Form, Input, Radio, DatePicker, Row, Col, Space, Typography } from 'antd'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCalendarAlt, faIdCard, faUser } from '@fortawesome/free-solid-svg-icons'
import { specialFieldValidation, getDateOfBirthFromID, hexToRGBA } from '../../../../lib/utils'
import dayjs from 'dayjs'
import SectionWithBorder from './SectionWithBorder'

const { Text } = Typography

const PatientDetailsForm = ({ form, specialFormState, setSpecialFormState, idType, setIdType, token }) => {
  // Custom selector component with embedded label and border
  const CustomSelector = ({ options, name, value, onChange }) => {
    const containerRef = useRef(null)

    // Handle option selection
    const handleSelect = (val) => {
      // Update the form value
      form.setFieldValue(name, val)

      // If there's an onChange handler, call it
      if (onChange) {
        onChange(val)
      }

      // Update the visual state directly
      if (containerRef.current) {
        // Get all option elements
        const optionElements = containerRef.current.querySelectorAll('.custom-option')

        // Add null check before using forEach
        if (optionElements && optionElements.length > 0) {
          // Reset all options to unselected state
          optionElements.forEach((el) => {
            el.style.backgroundColor = '#fff'
            el.style.color = 'rgba(0, 0, 0, 0.88)'
            el.style.borderColor = '#d9d9d9'
          })

          // Find the selected option and update its style
          const selectedOption = Array.from(optionElements).find((el) => el.getAttribute('data-value') === val)

          if (selectedOption) {
            selectedOption.style.backgroundColor = token.colorPrimary
            selectedOption.style.color = 'white'
            selectedOption.style.borderColor = token.colorPrimary
          }
        }
      }
    }

    // Set initial selection on mount and when value changes
    useEffect(() => {
      if (containerRef.current && value) {
        // Small delay to ensure DOM is ready
        setTimeout(() => {
          // Add null check before accessing querySelectorAll
          if (containerRef.current) {
            const optionElements = containerRef.current.querySelectorAll('.custom-option')

            // Add null check before using forEach
            if (optionElements && optionElements.length > 0) {
              // Reset all options
              optionElements.forEach((el) => {
                el.style.backgroundColor = '#fff'
                el.style.color = 'rgba(0, 0, 0, 0.88)'
                el.style.borderColor = '#d9d9d9'
              })

              // Find and highlight the selected option
              const selectedOption = Array.from(optionElements).find((el) => el.getAttribute('data-value') === value)

              if (selectedOption) {
                selectedOption.style.backgroundColor = token.colorPrimary
                selectedOption.style.color = 'white'
                selectedOption.style.borderColor = token.colorPrimary
              }
            }
          }
        }, 0)
      }
    }, [value, token.colorPrimary])

    // Common style for all option buttons
    const optionStyle = {
      display: 'inline-block',
      padding: '8px 16px',
      margin: '0 8px 8px 0',
      borderRadius: '8px',
      border: '1px solid #d9d9d9',
      cursor: 'pointer',
      fontSize: '14px',
      fontWeight: '500',
      transition: 'all 0.3s ease',
      whiteSpace: 'nowrap',
      backgroundColor: '#fff',
      color: 'rgba(0, 0, 0, 0.88)'
    }

    return (
      <div ref={containerRef} style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
        {options.map((option) => (
          <div
            key={option.value}
            className='custom-option'
            data-value={option.value}
            style={{
              ...optionStyle,
              backgroundColor: value === option.value ? token.colorPrimary : '#fff',
              color: value === option.value ? 'white' : 'rgba(0, 0, 0, 0.88)',
              borderColor: value === option.value ? token.colorPrimary : '#d9d9d9'
            }}
            onClick={() => handleSelect(option.value)}
          >
            {option.label}
          </div>
        ))}
      </div>
    )
  }

  // Gender options
  const genderOptions = [
    { value: 'male', label: 'Male' },
    { value: 'female', label: 'Female' },
    { value: 'other', label: 'Other' }
  ]

  return (
    <SectionWithBorder title='Patient Details' token={token} icon={faUser}>
      <Space direction='vertical' size={24} style={{ width: '100%' }}>
        {/* Name Fields */}
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12}>
            <Form.Item
              label={
                <Text strong style={{ fontSize: '15px' }}>
                  First Name
                </Text>
              }
              name='firstName'
              rules={[{ required: true, message: 'This field is required' }]}
            >
              <Input
                size='large'
                placeholder='e.g John'
                style={{ borderRadius: '8px' }}
                onChange={(e) => {
                  specialFieldValidation(form, specialFormState, setSpecialFormState, {
                    key: 'firstName',
                    event: e,
                    validationConfig: {
                      letters: { allowed: true, onlyCaps: false },
                      numbers: false,
                      spaces: true
                    }
                  })
                }}
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item
              label={
                <Text strong style={{ fontSize: '15px' }}>
                  Last Name
                </Text>
              }
              name='lastName'
              rules={[{ required: true, message: 'This field is required' }]}
            >
              <Input
                size='large'
                placeholder='e.g Doe'
                style={{ borderRadius: '8px' }}
                onChange={(e) => {
                  specialFieldValidation(form, specialFormState, setSpecialFormState, {
                    key: 'lastName',
                    event: e,
                    validationConfig: {
                      letters: { allowed: true, onlyCaps: false },
                      numbers: false,
                      spaces: true
                    }
                  })
                }}
              />
            </Form.Item>
          </Col>
        </Row>

        {/* ID Type Selection */}
        <Form.Item
          label={
            <Text strong style={{ fontSize: '15px' }}>
              ID Type
            </Text>
          }
        >
          <Radio.Group
            size='large'
            buttonStyle='solid'
            onChange={(e) => {
              form.setFieldValue('idNo', null)
              setIdType(e.target.value)
            }}
            value={idType}
          >
            <Radio.Button value={'id'} style={{ borderRadius: '8px 0 0 8px' }}>
              <FontAwesomeIcon icon={faIdCard} style={{ marginRight: '8px' }} />
              South African ID
            </Radio.Button>
            <Radio.Button value={'pass'} style={{ borderRadius: '0 8px 8px 0' }}>
              <FontAwesomeIcon icon={faIdCard} style={{ marginRight: '8px' }} />
              Passport
            </Radio.Button>
          </Radio.Group>
        </Form.Item>

        {/* ID Number Field */}
        <Row gutter={[16, 16]}>
          <Col xs={24}>
            {idType === 'id' ? (
              <Form.Item
                label={
                  <Text strong style={{ fontSize: '15px' }}>
                    South African ID Number
                  </Text>
                }
                name='idNo'
                rules={[
                  { required: true, message: 'This field is required' },
                  { min: 13, message: 'Invalid South African ID' }
                ]}
              >
                <Input
                  size='large'
                  placeholder='e.g. 790725*******'
                  maxLength={13}
                  style={{ borderRadius: '8px' }}
                  onChange={(e) => {
                    let idValue = specialFieldValidation(form, specialFormState, setSpecialFormState, {
                      key: 'idNo',
                      event: e,
                      validationConfig: {
                        letters: { allowed: false, onlyCaps: false },
                        numbers: true,
                        spaces: false
                      }
                    })

                    let dateOfBirth = getDateOfBirthFromID(idValue)
                    if (dateOfBirth) {
                      form.setFieldValue('dateOfBirth', dayjs(dateOfBirth))
                    }
                  }}
                />
              </Form.Item>
            ) : (
              <Form.Item
                label={
                  <Text strong style={{ fontSize: '15px' }}>
                    Passport Number
                  </Text>
                }
                name='idNo'
                rules={[{ required: true, message: 'This field is required' }]}
              >
                <Input
                  size='large'
                  placeholder='e.g. A1234567'
                  style={{ borderRadius: '8px' }}
                  onChange={(e) => {
                    specialFieldValidation(form, specialFormState, setSpecialFormState, {
                      key: 'idNo',
                      event: e,
                      validationConfig: {
                        letters: { allowed: true, onlyCaps: true },
                        numbers: true,
                        spaces: false
                      }
                    })
                  }}
                />
              </Form.Item>
            )}
          </Col>
        </Row>

        {/* Date of Birth and Gender */}
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12}>
            <Form.Item
              label={
                <Text strong style={{ fontSize: '15px' }}>
                  Date of Birth
                </Text>
              }
              name='dateOfBirth'
              rules={[{ required: true, message: 'This field is required' }]}
            >
              <DatePicker
                size='large'
                style={{ width: '100%', borderRadius: '8px' }}
                format='YYYY-MM-DD'
                suffixIcon={<FontAwesomeIcon icon={faCalendarAlt} color={token.colorPrimary} />}
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item
              label={
                <Text strong style={{ fontSize: '15px' }}>
                  Gender
                </Text>
              }
              name='gender'
              rules={[{ required: true, message: 'This field is required' }]}
            >
              <CustomSelector options={genderOptions} name='gender' value={form.getFieldValue('gender')} />
            </Form.Item>
          </Col>
        </Row>
      </Space>
    </SectionWithBorder>
  )
}

export default PatientDetailsForm
